import { useEffect, useState } from "react";
import SimpleModal, { BaseModalProps } from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import {
  getWeeklyCheckInCadenceSchedule,
  selectWeeklyCheckInCompanySettings,
  updateCheckInCadence,
} from "app/containers/WeeklyCheckInMonitor/slice";
import {
  WeeklyCheckInCadence,
  WeeklyCheckInCadenceSchedule,
} from "app/containers/WeeklyCheckInMonitor/types";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import Loading from "app/storybookComponents/Loading";
import { CADENCE_OPTIONS } from "../constants";

interface Props extends BaseModalProps {
  newCheckInCadence?: WeeklyCheckInCadence;
  existingCheckInCadence?: WeeklyCheckInCadence;
}

const formatUtcDate = (isoString?: string | null) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Status/confirmation modal for a cadence change — there is no "finish
// current cycle vs switch now" choice (see DBC-655 / DBC-698): the write
// either applies immediately or is rejected with a 409 while the current
// check-in window is open, applying automatically once it closes. Before
// letting the admin submit, this calls DBC-704's preview endpoint so we can
// show the real outcome up front instead of only finding out via the 409.
const UpdateCheckInCadenceModal = ({
  show,
  onHide,
  newCheckInCadence,
  existingCheckInCadence,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const inProgressCount = weeklyCheckInCompanySettings?.inProgressCount;

  const [schedule, setSchedule] = useState<WeeklyCheckInCadenceSchedule | null>(
    null
  );
  const [previewStatus, setPreviewStatus] = useState<
    "idle" | "loading" | "failed" | "succeeded"
  >("idle");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!show || !newCheckInCadence) {
      return;
    }

    let isStale = false;
    setPreviewStatus("loading");
    setSchedule(null);

    dispatch(getWeeklyCheckInCadenceSchedule(newCheckInCadence))
      .unwrap()
      .then((result) => {
        if (isStale) return;
        setSchedule(result);
        setPreviewStatus("succeeded");
      })
      .catch(() => {
        if (isStale) return;
        setPreviewStatus("failed");
      });

    return () => {
      isStale = true;
    };
  }, [dispatch, show, newCheckInCadence]);

  const onSave = async () => {
    if (!newCheckInCadence) return;

    setIsSaving(true);
    try {
      await dispatch(
        updateCheckInCadence({
          checkInFrequency: newCheckInCadence,
        })
      ).unwrap();
      onHide();
    } catch (err: unknown) {
      // 409 WINDOW_OPEN: the thunk passes { windowClosesUtc } via rejectWithValue.
      // Show the real date so the admin knows exactly when the change will kick in.
      const windowClosesUtc = (err as { windowClosesUtc?: string | null })
        ?.windowClosesUtc;
      const formattedCloseDate = formatUtcDate(windowClosesUtc);
      toast.error(
        formattedCloseDate
          ? `A check-in window is currently open and will close on ${formattedCloseDate}. Your cadence change will apply automatically at that point.`
          : "Couldn't update the check-in cadence. If a check-in window is currently open, this change will apply automatically once it closes.",
        { ...successNotificationOptions, className: "error" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const preview = schedule?.preview;
  const wouldBeBlocked = !!preview?.wouldBeBlocked;

  const getModalBody = () => {
    if (previewStatus === "loading") {
      return <Loading />;
    }

    // Preview failed to load — fall back to the generic outcome description
    // rather than block the admin from acting at all; the write endpoint
    // still enforces the block/apply rule server-side either way.
    if (previewStatus === "failed") {
      return (
        <p>
          This will apply immediately, unless a check-in window is currently
          open — in that case it will apply automatically once that window
          closes.
        </p>
      );
    }

    if (wouldBeBlocked) {
      const canApplyAtDate = formatUtcDate(preview?.canApplyAtUtc);
      const nextCycleStartDate = formatUtcDate(preview?.nextCycleStartUtc);
      return (
        <p>
          This change can't be made yet — a check-in window is currently open
          {typeof inProgressCount === "number" && inProgressCount > 0
            ? ` (${inProgressCount} ${
                inProgressCount === 1 ? "person is" : "people are"
              } mid check-in)`
            : ""}
          . It will apply automatically
          {canApplyAtDate ? ` on ${canApplyAtDate}` : ""}, and the next check-in
          will go out{nextCycleStartDate ? ` on ${nextCycleStartDate}` : ""}.
        </p>
      );
    }

    const nextCycleStartDate = formatUtcDate(preview?.nextCycleStartUtc);
    return (
      <p>
        Switching to {CADENCE_OPTIONS[newCheckInCadence ?? "monthly"]} now.
        {nextCycleStartDate
          ? ` The next check-in will be on ${nextCycleStartDate}.`
          : ""}
      </p>
    );
  };

  return (
    <SimpleModal show={show} onHide={onHide} title="Updating Check-In Cadence">
      <>
        <div>
          <p>
            You are changing the check-in cadence from{" "}
            {CADENCE_OPTIONS[existingCheckInCadence ?? "weekly"]} to{" "}
            {CADENCE_OPTIONS[newCheckInCadence ?? "monthly"]}.
          </p>
          {getModalBody()}
        </div>
        <div className="flex gap-2 ms-auto">
          <Button variant="secondary-gray" onClick={onHide}>
            {wouldBeBlocked ? "Close" : "Cancel"}
          </Button>
          {/* No override when blocked — DBC-698 still returns a 409 if
              submitted anyway, so this is purely informational. */}
          {!wouldBeBlocked && (
            <Button
              onClick={onSave}
              disabled={previewStatus === "loading" || isSaving}
            >
              Save
            </Button>
          )}
        </div>
      </>
    </SimpleModal>
  );
};

export default UpdateCheckInCadenceModal;
